/**
 * Email validation pattern
 * Matches: user@example.com
 * Pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Tax Identification Number validation pattern
 * Matches: 043336633 or 04-3336633
 * Pattern: 9 digits OR 2 digits, dash, 7 digits
 */
export const TAX_ID_PATTERN = /^(\d{9}|\d{2}-\d{7})$/;

/**
 * Zipcode validation pattern
 * Matches: exactly 5 digits
 * Pattern: /^\d{5}$/
 */
export const ZIPCODE_PATTERN = /^\d{5}$/;

/**
 * Validates if a string is a valid email address
 *
 * @param email - The email string to validate
 * @returns true if the email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  return EMAIL_PATTERN.test(email);
};

/**
 * Validates if a string is a valid Tax Identification Number
 * Format: 043336633 or 04-3336633 (9 digits OR 2 digits, dash, 7 digits)
 *
 * @param taxId - The tax identification number string to validate
 * @returns true if the tax ID is valid, false otherwise
 */
export const isValidTaxIdentificationNumber = (taxId: string): boolean => {
  if (!taxId || typeof taxId !== 'string') {
    return false;
  }
  return TAX_ID_PATTERN.test(taxId);
};

/**
 * Validates if a string is a valid zipcode
 * Format: exactly 5 digits
 *
 * @param zipcode - The zipcode string to validate
 * @returns true if the zipcode is valid, false otherwise
 */
export const isValidZipcode = (zipcode: string): boolean => {
  if (!zipcode || typeof zipcode !== 'string') {
    return false;
  }
  return ZIPCODE_PATTERN.test(zipcode);
};

interface IsValidDOBOptions {
  /**
   * When true, allows all fields to be empty (valid for optional DOB fields).
   * When false (default), all fields must be filled with valid values.
   */
  isOptional?: boolean;
}

/**
 * Validates if a date of birth is valid
 * Checks that all fields are present, numeric, within valid ranges, and form a valid date
 *
 * @param day - The day string (1-31)
 * @param month - The month string (1-12)
 * @param year - The year string (4 digits, 1900 to current year)
 * @param options - Optional configuration
 * @param options.isOptional - When true, allows all fields to be empty (default: false)
 * @returns true if the DOB is valid (or empty when isOptional is true), false otherwise
 */
export const isValidDOB = (
  day: string,
  month: string,
  year: string,
  options: IsValidDOBOptions = {},
): boolean => {
  const { isOptional = false } = options;
  const isAllEmpty = !day && !month && !year;

  // If optional and all fields are empty, that's valid
  if (isOptional && isAllEmpty) {
    return true;
  }

  // Check that all fields are present and not empty
  if (
    !day ||
    !month ||
    !year ||
    day.trim() === '' ||
    month.trim() === '' ||
    year.trim() === ''
  ) {
    return false;
  }

  // Check if year has 4 digits
  if (year.length !== 4) return false;

  const dayNum = parseInt(day);
  const monthNum = parseInt(month);
  const yearNum = parseInt(year);

  // Check for NaN values
  if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) return false;

  // Basic range checks
  if (dayNum < 1 || dayNum > 31) return false;
  if (monthNum < 1 || monthNum > 12) return false;
  if (yearNum < 1900 || yearNum > new Date().getFullYear()) return false;

  // Create date and validate it's actually valid
  const testDate = new Date(yearNum, monthNum - 1, dayNum);
  return (
    testDate.getFullYear() === yearNum &&
    testDate.getMonth() === monthNum - 1 &&
    testDate.getDate() === dayNum
  );
};

/**
 * Validates if a full name string contains at least a valid first name
 * Extracts the first name (text before first space, or entire string if no space)
 * and checks that it has at least one character
 *
 * @param fullName - The full name string to validate
 * @returns true if the name is valid (contains at least a first name), false otherwise
 */
export const isNameValid = (fullName: string): boolean => {
  const trimmed = fullName.trim();
  if (!trimmed) return false;

  const spaceIndex = trimmed.indexOf(' ');
  const firstName = spaceIndex > 0 ? trimmed.substring(0, spaceIndex) : trimmed;

  return firstName.length > 0;
};
